import React, { useEffect, useState } from 'react';

/**
 * Live elapsed-time timer for an active match.
 * startedAt : ISO string of when match started (match.started_at)
 * endedAt   : ISO string of when match ended (match.completed_at) — freezes the timer when set
 * compact   : if true, shows only "32:15" without label
 * style     : extra inline styles for the outer element
 */
export default function MatchTimer({ startedAt, endedAt = null, compact = false, style = {} }) {
  const [elapsed, setElapsed] = useState(calcElapsed(startedAt, endedAt));

  useEffect(() => {
    if (!startedAt) return;
    // If match has ended, show frozen time — no interval needed
    if (endedAt) {
      setElapsed(calcElapsed(startedAt, endedAt));
      return;
    }
    const id = setInterval(() => setElapsed(calcElapsed(startedAt, null)), 1000);
    return () => clearInterval(id);
  }, [startedAt, endedAt]);

  if (!startedAt) return null;

  const { h, m, s } = elapsed;
  const display = h > 0
    ? `${h}:${pad(m)}:${pad(s)}`
    : `${pad(m)}:${pad(s)}`;

  if (compact) {
    return (
      <span style={{ fontVariantNumeric: 'tabular-nums', fontFamily: 'monospace', ...style }}>
        {display}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', ...style }}>
      <span style={{ fontSize: '13px' }}>⏱</span>
      <span style={{
        fontVariantNumeric: 'tabular-nums',
        fontFamily: 'monospace',
        fontWeight: '700',
      }}>
        {display}
      </span>
    </div>
  );
}

function calcElapsed(startedAt, endedAt) {
  if (!startedAt) return { h: 0, m: 0, s: 0 };
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const diffMs = Math.max(0, end - new Date(startedAt).getTime());
  const totalSec = Math.floor(diffMs / 1000);
  return {
    h: Math.floor(totalSec / 3600),
    m: Math.floor((totalSec % 3600) / 60),
    s: totalSec % 60,
  };
}

function pad(n) {
  return String(n).padStart(2, '0');
}
